import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
  Stack,
  Paper
} from '@mui/material';
import {
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { format, addDays, isToday, isTomorrow, isYesterday } from 'date-fns';
import axiosInstance from '../utils/axiosConfig';

interface Personnel {
  _id: string;
  name: string;
  email: string;
  roles: string[];
  profileImage?: string;
}

interface TodayRotaEntry {
  _id: string;
  personnelId: Personnel | string;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  notes?: string;
  personnelName?: string;
  personnelObj?: Personnel;
  startMinutes: number;
  endMinutes: number;
  duration: number;
}

interface AllHandsEntry {
  _id: string;
  title: string;
  startTime: string;
  endTime: string;
  startMinutes: number;
  endMinutes: number;
}

interface WhosWorkingTodayProps {
  variant?: 'timeline' | 'compact';
  refreshInterval?: number;
  onStaffClick?: (personnel: Personnel) => void;
  showTitle?: boolean;
  showAllStatuses?: boolean;
}

const WhosWorkingToday: React.FC<WhosWorkingTodayProps> = ({
  variant = 'timeline',
  refreshInterval = 5,
  onStaffClick,
  showTitle = true,
  showAllStatuses = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [entries, setEntries] = useState<TodayRotaEntry[]>([]);
  const [allHandsMeetings, setAllHandsMeetings] = useState<AllHandsEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const TIMELINE_START = 8;
  const TIMELINE_END = 23;
  const TIMELINE_HOURS = TIMELINE_END - TIMELINE_START;

  const timeToMinutes = (t: string) => t.split(':').reduce((acc, v, i) => acc + Number(v) * (i === 0 ? 60 : 1), 0);
  const nowMinutes = () => new Date().getHours() * 60 + new Date().getMinutes();
  const posPercent = (m: number) => {
    const start = TIMELINE_START * 60;
    const end = TIMELINE_END * 60;
    return Math.max(0, Math.min(100, ((m - start) / (end - start)) * 100));
  };

  const getDateLabel = (d: Date) =>
    isToday(d) ? 'Today' : isTomorrow(d) ? 'Tomorrow' : isYesterday(d) ? 'Yesterday' : format(d, 'EEEE, MMM d');

  const changeDay = (delta: number) => setSelectedDate(d => addDays(d, delta));
  const goToday = () => setSelectedDate(new Date());

  const statusConfig: Record<string, { label: string; color: string; priority: number; show: boolean }> = {
    working: { label: 'Working', color: '#ec4899', priority: 10, show: true },
    programming: { label: 'Programming', color: '#8b5cf6', priority: 9, show: true },
    event: { label: 'Event', color: '#a855f7', priority: 8, show: true },
    cooler: { label: 'Cooler', color: '#60a5fa', priority: 7, show: true },
    abroad: { label: 'Abroad', color: '#2563eb', priority: 6, show: true },
    meeting: { label: 'Meeting', color: '#1d4ed8', priority: 6, show: true },
    'time-lieu': { label: 'TIL', color: '#ca8a04', priority: 7, show: true },
    am: { label: 'AM', color: '#059669', priority: 5, show: true },
    pm: { label: 'PM', color: '#84cc16', priority: 4, show: true },
    traveling: { label: 'Traveling', color: '#eab308', priority: 3, show: showAllStatuses },
    'annual-leave': { label: 'Annual Leave', color: '#10b981', priority: 2, show: showAllStatuses },
    'sick-leave': { label: 'Sick Leave', color: '#374151', priority: 2, show: showAllStatuses },
    unavailable: { label: 'Unavailable', color: '#dc2626', priority: 4, show: true },
    'not-working': { label: 'Off', color: '#6b7280', priority: 0, show: showAllStatuses }
  };

  const fetchRota = async (date: Date) => {
    setLoading(true);
    setError(null);
    try {
      const ds = format(date, 'yyyy-MM-dd');
      const [people, rota] = await Promise.all([
        axiosInstance.get('/api/rota/personnel'),
        axiosInstance.get('/api/rota', { params: { startDate: ds, endDate: ds } })
      ]);
      const allHandsRaw = rota.data.filter((e: any) => e.isAllHands);
      const personalRaw = rota.data.filter((e: any) => !e.isAllHands);

      const enriched: TodayRotaEntry[] = personalRaw.map((e: any) => {
        const person = typeof e.personnelId === 'string'
          ? people.data.find((p: Personnel) => p._id === e.personnelId)
          : e.personnelId;
        const sMin = timeToMinutes(e.startTime);
        const eMin = timeToMinutes(e.endTime);
        return {
          ...e,
          personnelObj: person,
          personnelName: person?.name || 'Unknown',
          startMinutes: sMin,
          endMinutes: eMin,
          duration: eMin - sMin
        };
      }).filter((e: TodayRotaEntry): e is TodayRotaEntry => !!e.personnelObj);

      const meetings: AllHandsEntry[] = allHandsRaw.map((e: any) => ({
        _id: e._id,
        title: e.title || 'Staff Meeting',
        startTime: e.startTime,
        endTime: e.endTime,
        startMinutes: timeToMinutes(e.startTime),
        endMinutes: timeToMinutes(e.endTime)
      }));

      setEntries(enriched);
      setAllHandsMeetings(meetings);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      setError('Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRota(selectedDate); }, [selectedDate]);

  useEffect(() => {
    if (isToday(selectedDate) && refreshInterval > 0) {
      const id = setInterval(() => fetchRota(selectedDate), refreshInterval * 60000);
      return () => clearInterval(id);
    }
  }, [selectedDate, refreshInterval]);

  const visible = entries.filter(e => statusConfig[e.status]?.show);
  const grouped = visible.reduce((acc, e) => {
    const id = e.personnelObj!._id;
    if (!acc[id]) acc[id] = { person: e.personnelObj!, blocks: [] as TodayRotaEntry[] };
    acc[id].blocks.push(e);
    return acc;
  }, {} as Record<string, { person: Personnel; blocks: TodayRotaEntry[] }>);

  const curMin = nowMinutes();
  const showCur = isToday(selectedDate) && curMin >= TIMELINE_START * 60 && curMin <= TIMELINE_END * 60;
  const curPos = posPercent(curMin);
  const hours = Array.from({ length: TIMELINE_HOURS + 1 }, (_, i) => TIMELINE_START + i);

  if (loading) return <Card><CardContent><CircularProgress /> Loading...</CardContent></Card>;
  if (error) return <Card><CardContent><Alert severity="error">{error}</Alert></CardContent></Card>;

  // Mobile Card View
  const MobileCardView = () => (
    <Stack spacing={2}>
      {Object.values(grouped).map(({ person, blocks }) => (
        <Paper key={person._id} elevation={1} sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" mb={1}>
            <Avatar 
              src={person.profileImage} 
              sx={{ width: 40, height: 40, mr: 2, cursor: onStaffClick ? 'pointer' : 'default' }} 
              onClick={() => onStaffClick?.(person)}
            >
              {person.name[0]}
            </Avatar>
            <Box flexGrow={1}>
              <Typography variant="h6" noWrap>{person.name}</Typography>
              {showCur && (
                <Box display="flex" alignItems="center" mt={0.5}>
                  <AccessTimeIcon fontSize="small" sx={{ mr: 0.5, color: 'info.main' }} />
                  <Typography variant="caption" color="info.main" fontWeight="bold">
                    Current Time: {format(new Date(), 'HH:mm')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {blocks.map((block, idx) => (
            <Box key={idx} mb={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Chip 
                  label={statusConfig[block.status]?.label || block.status}
                  size="small"
                  sx={{ 
                    bgcolor: statusConfig[block.status]?.color || '#888',
                    color: '#fff',
                    fontWeight: 'bold'
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {block.startTime} - {block.endTime}
                </Typography>
              </Box>
              {block.notes && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {block.notes}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      ))}
    </Stack>
  );

  // Tablet Timeline
  const TabletTimelineView = () => {
    const tabletHours = hours.filter((_, idx) => idx % 2 === 0);
    
    return (
      <Box>
        <Box position="relative" mb={2} py={1}>
          <Box display="flex" justifyContent="space-between" mb={1}>
            {tabletHours.map(h => (
              <Typography key={h} variant="caption" fontSize="0.7rem">
                {h === 12 ? '12pm' : h > 12 ? `${h - 12}pm` : `${h}am`}
              </Typography>
            ))}
          </Box>
          <Box height={2} bgcolor="grey.500" borderRadius={1} position="relative">
            {showCur && (
              <Box
                position="absolute"
                left={`${curPos}%`}
                top={-4}
                width={2}
                height={10}
                bgcolor="error.main"
                zIndex={100}
              />
            )}
          </Box>
        </Box>

        <Stack spacing={1.5}>
          {allHandsMeetings.map(meeting => {
            const sP = posPercent(meeting.startMinutes);
            const wP = posPercent(meeting.endMinutes) - sP;
            return (
              <Box key={meeting._id} display="flex" alignItems="center">
                <Box minWidth="124px" mr={1}>
                  <Typography variant="caption" noWrap sx={{ color: '#c4b5fd', fontWeight: 'bold' }}>{meeting.title}</Typography>
                </Box>
                <Tooltip title={`${meeting.title} (${meeting.startTime}-${meeting.endTime})`} placement="top">
                  <Box position="relative" flexGrow={1} height={6} bgcolor="#1e1e1e" borderRadius={1}>
                    <Box
                      position="absolute"
                      left={`${sP}%`}
                      width={`${wP}%`}
                      height="100%"
                      bgcolor="rgba(109,40,217,0.4)"
                      borderRadius={1}
                      sx={{ border: '1px solid #6d28d9' }}
                    />
                  </Box>
                </Tooltip>
              </Box>
            );
          })}
          {Object.values(grouped).map(({ person, blocks }) => (
            <Box key={person._id} display="flex" alignItems="center">
              <Avatar 
                src={person.profileImage} 
                sx={{ width: 24, height: 24, mr: 1, cursor: onStaffClick ? 'pointer' : 'default' }} 
                onClick={() => onStaffClick?.(person)}
              >
                {person.name[0]}
              </Avatar>
              <Box minWidth="100px" mr={1}>
                <Typography variant="caption" noWrap>{person.name}</Typography>
              </Box>
              <Box position="relative" flexGrow={1} height={6} bgcolor="#1e1e1e" borderRadius={1}>
{blocks.map((e, idx) => {
  const sP = posPercent(e.startMinutes);
  const wP = posPercent(e.endMinutes) - sP;
  const color = statusConfig[e.status]?.color || '#888';
  return (
    <Tooltip 
      key={idx}
      title={`${statusConfig[e.status]?.label || e.status} (${e.startTime}-${e.endTime})`}
      placement="top"
    >
      <Box 
        position="absolute" 
        left={`${sP}%`} 
        width={`${wP}%`} 
        height="100%" 
        bgcolor={color} 
        borderRadius={1} 
      />
    </Tooltip>
  );
})}
                {showCur && (
                  <Box
                    position="absolute"
                    left={`${curPos}%`}
                    top={-2}
                    width={2}
                    height={10}
                    bgcolor="error.main"
                    zIndex={100}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  // Desktop Full Timeline
  const DesktopTimelineView = () => (
    <Box 
      sx={{
        display: 'grid',
        gridTemplateColumns: '40px 1fr 8fr',
        gap: '8px',
        position: 'relative'
      }}
    >
      <Box></Box>
      <Box></Box>
      <Box position="relative" py={1}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          {hours.map(h => <Typography key={h} variant="caption">{h === 12 ? '12pm' : h > 12 ? `${h - 12}pm` : `${h}am`}</Typography>)}
        </Box>
        <Box height={2} bgcolor="grey.500" borderRadius={1}>
          {showCur && (
            <Box
              position="absolute"
              left={`${curPos}%`}
              top={-4}
              width={2}
              height={30}
              bgcolor="error.main"
              zIndex={100}
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ gridColumn: '1 / -1', my: 1 }} />

      {allHandsMeetings.map(meeting => {
        const sP = posPercent(meeting.startMinutes);
        const wP = posPercent(meeting.endMinutes) - sP;
        return (
          <React.Fragment key={meeting._id}>
            <Box />
            <Box display="flex" alignItems="center">
              <Typography noWrap variant="body2" sx={{ fontWeight: 'bold', color: '#c4b5fd' }}>{meeting.title}</Typography>
            </Box>
            <Tooltip title={`${meeting.title} (${meeting.startTime}-${meeting.endTime})`} placement="top">
              <Box position="relative" height={8} bgcolor="#1e1e1e" borderRadius={1}>
                <Box
                  position="absolute"
                  left={`${sP}%`}
                  width={`${wP}%`}
                  height="100%"
                  bgcolor="rgba(109,40,217,0.4)"
                  borderRadius={1}
                  sx={{ border: '1px solid #6d28d9' }}
                />
              </Box>
            </Tooltip>
          </React.Fragment>
        );
      })}

      {allHandsMeetings.length > 0 && <Divider sx={{ gridColumn: '1 / -1', my: 0.5, borderStyle: 'dashed', borderColor: '#6d28d9', opacity: 0.4 }} />}

      {Object.values(grouped).map(({ person, blocks }) => (
        <React.Fragment key={person._id}>
          <Avatar 
            src={person.profileImage} 
            sx={{ width: 28, height: 28, cursor: onStaffClick ? 'pointer' : 'default' }} 
            onClick={() => onStaffClick?.(person)}
          >
            {person.name[0]}
          </Avatar>
          
          <Box display="flex" alignItems="center">
            <Typography noWrap variant="body2">{person.name}</Typography>
          </Box>
          
          <Box position="relative" height={8} bgcolor="#1e1e1e" borderRadius={1}>
{blocks.map((e, idx) => {
  const sP = posPercent(e.startMinutes);
  const wP = posPercent(e.endMinutes) - sP;
  const color = statusConfig[e.status]?.color || '#888';
  return (
    <Tooltip 
      key={idx}
      title={`${statusConfig[e.status]?.label || e.status} (${e.startTime}-${e.endTime})`}
      placement="top"
    >
      <Box 
        position="absolute" 
        left={`${sP}%`} 
        width={`${wP}%`} 
        height="100%" 
        bgcolor={color} 
        borderRadius={1} 
      />
    </Tooltip>
  );
})}
            {showCur && (
              <Box
                position="absolute"
                left={`${curPos}%`}
                top={-4}
                width={2}
                height={16}
                bgcolor="error.main"
                zIndex={100}
              />
            )}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );

  return (
    <Card>
      <CardContent sx={{ pb: isMobile && variant === 'timeline' ? 2 : 1 }}>
        {showTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PeopleIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              {isMobile ? 'Rota' : variant === 'compact' ? 'Roster' : "Today's Rota"}
            </Typography>
            <Box ml="auto" display="flex" alignItems="center">
              <IconButton size={isMobile ? "small" : "medium"} onClick={() => changeDay(-1)}>
                <ChevronLeftIcon />
              </IconButton>
              <Tooltip title={!isToday(selectedDate) ? 'Go to today' : ''}>
                <Typography 
                  onClick={!isToday(selectedDate) ? goToday : undefined} 
                  sx={{ 
                    mx: 1, 
                    cursor: !isToday(selectedDate) ? 'pointer' : 'default',
                    fontSize: isMobile ? '0.875rem' : 'inherit'
                  }}
                >
                  {getDateLabel(selectedDate)}
                </Typography>
              </Tooltip>
              <IconButton size={isMobile ? "small" : "medium"} onClick={() => changeDay(1)}>
                <ChevronRightIcon />
              </IconButton>
              {!isMobile && (
                <Tooltip title={`Last updated ${format(lastUpdated, 'HH:mm')}`}>
                  <ScheduleIcon fontSize="small" sx={{ ml: 1 }} />
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

        {/* Responsive Layout Logic */}
        {isMobile && variant === 'timeline' && <MobileCardView />}
        
        {isMobile && variant === 'compact' && (
          <Stack spacing={1}>
            {Object.values(grouped).map(({ person, blocks }) => (
              <Box key={person._id} display="flex" alignItems="center" py={1} px={2} bgcolor="background.paper" borderRadius={1}>
                <Avatar 
                  src={person.profileImage} 
                  sx={{ width: 32, height: 32, mr: 2, cursor: onStaffClick ? 'pointer' : 'default' }} 
                  onClick={() => onStaffClick?.(person)}
                >
                  {person.name[0]}
                </Avatar>
                <Box flexGrow={1} minWidth={0}>
                  <Typography variant="body2" fontWeight="medium" noWrap>{person.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {blocks.length > 0 && (
                      `${statusConfig[blocks[0].status]?.label} • ${blocks[0].startTime}-${blocks[0].endTime}`
                    )}
                  </Typography>
                </Box>
                {blocks.length > 0 && (
                  <Box 
                    width={12} 
                    height={12} 
                    borderRadius="50%" 
                    bgcolor={statusConfig[blocks[0].status]?.color || '#888'}
                  />
                )}
              </Box>
            ))}
          </Stack>
        )}

        {isTablet && !isMobile && variant === 'timeline' && <TabletTimelineView />}

        {!isMobile && !isTablet && variant === 'compact' && (
          <>
            {visible.length === 0 ? (
              <Typography>No one scheduled {isToday(selectedDate) ? 'today' : 'on this day'}.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(visible.reduce((a, e) => {
                  a[e.status] = (a[e.status] || 0) + 1;
                  return a;
                }, {} as Record<string, number>)).map(([status, count]) => (
                  <Chip 
                    key={status} 
                    label={`${statusConfig[status].label} (${count})`} 
                    size="small" 
                    sx={{ bgcolor: statusConfig[status].color, color: '#000' }} 
                  />
                ))}
              </Box>
            )}
          </>
        )}

        {!isMobile && !isTablet && variant === 'timeline' && <DesktopTimelineView />}

        {!isMobile && (
          <Typography variant="caption" color="text.secondary" textAlign="center" display="block" mt={2}>
            {isToday(selectedDate) && refreshInterval > 0 && `Auto-updates every ${refreshInterval} mins • `}
            {format(selectedDate, isMobile ? 'EEE, MMM d' : 'EEEE, MMMM d, yyyy')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default WhosWorkingToday;